import { useQuery } from "@tanstack/react-query";
;
import { api,resolveUrl } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card,CardContent,CardHeader } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { ExternalLink,Package,Truck,X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useOrganizationShop } from "@/hooks/use-organization-shop";
import { queryKeys } from "@/lib/query-keys";
import {
Tooltip,
TooltipContent,
TooltipProvider,
TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatInStoreTz } from '@/lib/time';

interface Transfer {
  id: string;
  direction?: string;
  manifestNumber?: string | number;
  metrcId?: string;
  recipientFacilityName?: string;
  shipperFacilityName?: string;
  recipientFacilityLicenseNumber?: string;
  shipperFacilityLicenseNumber?: string;
  receivedDate?: string;
  createdDateTime?: string;
  manifestDate?: string;
  transferTypeName?: string;
  transferType?: string;
  createdByUserName?: string;
  rawData?: Record<string, unknown>;
}

interface DeliveryRecord {
  id: string;
  shippedDate?: string;
  receivedDate?: string;
  receivedBy?: string;
  estimatedDepartureDateTime?: string;
  estimatedArrivalDateTime?: string;
  actualDepartureDateTime?: string | null;
  actualArrivalDateTime?: string | null;
  receivedDateTime?: string | null;
  rawData?: Record<string, unknown>;
  packages?: DeliveryPackageRecord[];
  _count?: { packages?: number };
}

interface DeliveryPackageRecord {
  id: string;
  label?: string;
  packageLabel?: string;
  itemName?: string;
  quantity?: string | number;
  unitOfMeasureName?: string;
  unitOfMeasureAbbr?: string;
  packageStateName?: string;
  metrcPackage?: { label?: string; itemName?: string };
  rawData?: Record<string, unknown>;
}

interface TransferSidePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transfer: Transfer | null;
}

export function TransferSidePanel({
  open,
  onOpenChange,
  transfer,
}: TransferSidePanelProps) {
  const { shopId } = useOrganizationShop();

  const deliveriesQuery = useQuery<DeliveryRecord[]>({
    queryKey: queryKeys.transferDeliveries(transfer?.id ?? "", shopId ?? ""),
    queryFn: async () => {
      const res = await api.get<DeliveryRecord[] | { data?: DeliveryRecord[] }>(`/metrc/transfers/${transfer!.id}/deliveries`, {
        params: { shopId },
      });
      const deliveryData: DeliveryRecord[] = Array.isArray(res)
        ? res
        : (res.data ?? []);

      await Promise.all(
        deliveryData.map(async (delivery) => {
          if (Array.isArray(delivery.packages) && delivery.packages.length > 0)
            return;
          const pkgRes = await api.get<
            NonNullable<DeliveryRecord['packages']> | { data?: NonNullable<DeliveryRecord['packages']> }
          >(
            `/metrc/transfers/deliveries/${delivery.id}/packages`,
            { params: { shopId } },
          );
          delivery.packages = Array.isArray(pkgRes)
            ? pkgRes
            : (pkgRes.data ?? []);
        }),
      );
      return deliveryData;
    },
    enabled: !!transfer && !!shopId && open,
  });

  if (!transfer || !open) return null;

  const deliveries = deliveriesQuery.data ?? [];
  const loadingDetails = deliveriesQuery.isLoading;

  const raw = transfer.rawData as
    | Record<string, string | undefined>
    | undefined;
  const manifestNumber =
    raw?.ManifestNumber ??
    String(transfer.manifestNumber ?? transfer.metrcId ?? "—");
  const facilityName =
    transfer.direction === "OUTGOING"
      ? (transfer.recipientFacilityName ??
        raw?.RecipientFacilityName ??
        "Unknown Facility")
      : (transfer.shipperFacilityName ??
        raw?.ShipperFacilityName ??
        "Unknown Facility");
  const license =
    transfer.direction === "OUTGOING"
      ? (transfer.recipientFacilityLicenseNumber ??
        raw?.RecipientFacilityLicenseNumber)
      : (transfer.shipperFacilityLicenseNumber ??
        raw?.ShipperFacilityLicenseNumber);
  const manifestDate =
    transfer.receivedDate ??
    raw?.ReceivedDateTime ??
    transfer.createdDateTime ??
    raw?.CreatedDateTime ??
    transfer.manifestDate;
  const transferType =
    transfer.transferTypeName ??
    raw?.ShipmentTypeName ??
    transfer.transferType ??
    "Transfer";

  return (
    <Card className="w-full h-full border shadow-lg rounded-xl flex flex-col overflow-hidden animate-in slide-in-from-right-10 fade-in duration-300 relative bg-card">
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2 bg-background z-10 border-b relative shrink-0">
        <div className="space-y-1">
          <h2 className="text-lg font-bold tracking-tight">
            #{manifestNumber}
          </h2>
          <div className="text-sm text-muted-foreground">
            From: {facilityName}
            {license ? ` (${license})` : ""}
          </div>
          <div className="text-sm text-muted-foreground">
            Date:{" "}
            {manifestDate
              ? formatInStoreTz(manifestDate, "MMMM dd, yyyy")
              : "N/A"}
          </div>
          <div className="text-sm text-muted-foreground">
            Type: {transferType}
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onOpenChange(false)}
          className="transition-transform duration-200"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </Button>
      </CardHeader>

      <CardContent className="p-6 space-y-6 overflow-y-auto bg-background/50">
        <section className="space-y-3">
          <h3 className="text-xs font-semibold text-muted-foreground tracking-wider uppercase">
            DELIVERIES ({deliveries.length})
          </h3>

          {loadingDetails ? (
            <div className="space-y-4">
              {Array.from({ length: 2 }).map((_, idx) => (
                <div
                  key={idx}
                  className="rounded-lg border bg-card/80 p-4 space-y-4"
                >
                  <div className="flex items-center justify-between">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-5 w-20 rounded-full" />
                  </div>
                  <div className="space-y-2">
                    <Skeleton className="h-3 w-48" />
                    <Skeleton className="h-3 w-40" />
                  </div>
                  <Separator />
                  <div className="space-y-2">
                    <Skeleton className="h-3 w-24" />
                    <div className="rounded-md border overflow-hidden">
                      <div className="grid grid-cols-[1fr_1fr_80px_60px] text-xs bg-muted/50 px-3 py-2">
                        {Array.from({ length: 4 }).map((_, i) => (
                          <Skeleton key={i} className="h-3 w-16" />
                        ))}
                      </div>
                      {Array.from({ length: 3 }).map((_, rowIdx) => (
                        <div
                          key={rowIdx}
                          className="grid grid-cols-[1fr_1fr_80px_60px] px-3 py-2 text-sm border-t"
                        >
                          {Array.from({ length: 4 }).map((_, i) => (
                            <Skeleton key={i} className="h-3 w-16" />
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : deliveries.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              No deliveries found.
            </div>
          ) : (
            deliveries.map((delivery, idx) => {
              const packages = delivery.packages ?? [];
              const rawD = delivery.rawData as
                | Record<string, string | undefined>
                | undefined;
              const shippedDate =
                delivery.actualDepartureDateTime ??
                rawD?.ActualDepartureDateTime ??
                delivery.estimatedDepartureDateTime ??
                rawD?.EstimatedDepartureDateTime;
              const arrivedDate =
                delivery.actualArrivalDateTime ??
                rawD?.ActualArrivalDateTime ??
                delivery.receivedDateTime ??
                rawD?.ReceivedDateTime ??
                delivery.estimatedArrivalDateTime ??
                rawD?.EstimatedArrivalDateTime;
              const receivedBy =
                delivery.receivedBy ??
                rawD?.CreatedByUserName ??
                rawD?.DriverName ??
                transfer.createdByUserName ??
                "N/A";
              const deliveryPackageCount =
                delivery._count?.packages ??
                (rawD?.PackageCount !== undefined
                  ? Number(rawD.PackageCount)
                  : undefined) ??
                packages.length;

              return (
                <div
                  key={delivery.id}
                  className="rounded-lg border bg-card/80 p-4 space-y-4"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium">
                      Delivery #{idx + 1}
                    </div>
                    <Badge
                      variant="secondary"
                      className="text-[10px] h-5 px-1.5 font-normal"
                    >
                      {deliveryPackageCount} Packages
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground space-y-1">
                    <div className="flex items-center gap-2">
                      <Truck className="h-3 w-3" />
                      Shipped:{" "}
                      {shippedDate
                        ? formatInStoreTz(shippedDate, "MMM dd")
                        : "N/A"}
                      <span className="text-muted-foreground">→</span>
                      Arrived:{" "}
                      {arrivedDate
                        ? formatInStoreTz(arrivedDate, "MMM dd")
                        : "N/A"}
                    </div>
                    <div className="flex items-center gap-2">
                      <Package className="h-3 w-3" />
                      Received by: {receivedBy}
                    </div>
                  </div>

                  <Separator />

                  <div className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">
                    Packages
                  </div>
                  <div className="rounded-md border overflow-hidden">
                    <div className="grid grid-cols-[1fr_1fr_80px_60px] text-xs bg-muted/50 px-3 py-2">
                      <div>Label</div>
                      <div>Item</div>
                      <div className="text-right">Qty</div>
                      <div className="text-center">St</div>
                    </div>
                    <div className="max-h-[220px] overflow-y-auto">
                      {packages.length === 0 ? (
                        <div className="p-4 text-sm text-muted-foreground">
                          No packages found.
                        </div>
                      ) : (
                        packages.map((pkg) => {
                          const rawP = pkg.rawData as
                            | Record<string, unknown>
                            | undefined;
                          const pkgLabel =
                            pkg.packageLabel ??
                            rawP?.PackageLabel ??
                            pkg.metrcPackage?.label ??
                            pkg.label ??
                            "—";
                          const pkgItem =
                            pkg.itemName ??
                            rawP?.ItemName ??
                            pkg.metrcPackage?.itemName ??
                            "Unknown";
                          const qty =
                            rawP?.ReceivedQuantity ?? pkg.quantity ?? "0";
                          const uom =
                            rawP?.ReceivedUnitOfMeasureName ??
                            pkg.unitOfMeasureName ??
                            pkg.unitOfMeasureAbbr ??
                            "";
                          const state =
                            pkg.packageStateName ??
                            rawP?.ShipmentPackageState ??
                            "Unknown";
                          const isAccepted =
                            String(state).toLowerCase() === "accepted";
                          return (
                            <div
                              key={pkg.id}
                              className="grid grid-cols-[1fr_1fr_80px_60px] px-3 py-2 text-sm border-t items-center"
                            >
                              <div className="font-mono text-xs truncate pr-2">
                                <TooltipProvider delayDuration={300}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="cursor-help">{String(pkgLabel)}</span>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">
                                      <p className="max-w-xs break-all font-mono text-[10px]">{String(pkgLabel)}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                              <div className="font-mono text-xs truncate pr-2">
                                <TooltipProvider delayDuration={300}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="cursor-help">{String(pkgItem)}</span>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">
                                      <p className="max-w-xs break-words">{String(pkgItem)}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                              <div className="text-right font-mono text-xs">
                                {String(qty)}
                                {uom ? ` ${uom}` : ""}
                              </div>
                              <div className="text-center">
                                <span
                                  className={cn(
                                    "text-xs",
                                    isAccepted
                                      ? "text-emerald-500"
                                      : "text-red-500",
                                  )}
                                >
                                  {isAccepted ? "✓" : "✗"}
                                </span>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </section>

        <Button
          variant="outline"
          className="w-full"
          onClick={() =>
            window.open(
              resolveUrl(
                `/metrc/transfers/manifest/${transfer.id}?shopId=${shopId}`,
              ),
              "_blank",
            )
          }
        >
          <ExternalLink className="h-4 w-4 mr-2" /> View Manifest PDF
        </Button>
      </CardContent>
    </Card>
  );
}
